import { AxiosRequestConfig } from 'axios';

import { AuthenticatedApiService } from '@/services/api/BaseApiService';

import { MolecularLabTestStatus } from '../features/user/types';
import {
  CancerGrade,
  CurrentStage,
  CurrentStatusType,
  EligibilityStatus,
  EnrolmentStep,
  GenderAtBirth,
  HistopathologicalType,
  MetastasesPresent,
  PerformanceStatus,
  PrimaryCancerTissueOfOrigin,
  Title,
} from './enums';
import { LabTest, Package, PatientDocumentType } from './types';

export interface Metadata {
  currentStage: EnumItem<CurrentStage>[];
  enrolmentStep: EnumItem<EnrolmentStep>[];
  primaryCancerTissueOfOrigin: EnumItem<PrimaryCancerTissueOfOrigin>[];
  histopathologicalType: EnumItem<HistopathologicalType>[];
  metastasesPresent: EnumItem<MetastasesPresent>[];
  performanceStatus: EnumItem<PerformanceStatus>[];
  currentStatusType: EnumItem<CurrentStatusType>[];
  patientDocumentType: EnumItem<PatientDocumentType>[];
  cancerGrade: EnumItem<CancerGrade>[];
  genderAtBirth: EnumItem<GenderAtBirth>[];
  title: EnumItem<Title>[];
  eligibilityStatus: EnumItem<EligibilityStatus>[];
  primaryCancerToHistoMapping: PrimaryCancerToHistoMapping[];
  package: EnumItem<Package>[];
  marketingSource: EnumItem<number>[];
  emailState: EnumItem<number>[];
  invoiceFrequency: EnumItem<number>[];
  warmth: EnumItem<number>[];
  clinicianActivityState: EnumItem<number>[];
  clinicianFunnelState: EnumItem<number>[];
  molecularLabTestStatus: EnumItem<MolecularLabTestStatus>[];
  usStates: UsStateRecord[];
  usStatesNotAllowedToTrade: UsStateRecord[];
  packageToLabTestMapping: PackageToLabTestMapping[];
  countries: Country[];
  alterationType: EnumItem<number>[];
  functionalConsequence: EnumItem<number>[];
}

interface Country {
  code: string;
  name: string;
}

export interface UsStateRecord {
  code: string;
  name: string;
}

interface PrimaryCancerToHistoMapping {
  primaryCancer: PrimaryCancerTissueOfOrigin;
  histopathologicalTypes: HistopathologicalType[];
}

interface PackageToLabTestMapping {
  package: Package;
  labTests: PackageLabTest[];
}

interface PackageLabTest {
  value: LabTest;
  description: string;
}

class MetadataApiService extends AuthenticatedApiService {
  private readonly _metadataCacheKey = 'metadata-cache';
  private readonly _metadataCacheTime = 60 * 60 * 1000; // 1 hour

  public getMetadata = (forceRefresh?: boolean, config?: AxiosRequestConfig) => {
    if (forceRefresh) {
      this.deleteCache(this._metadataCacheKey);
    }

    return this.getCached<Metadata>(
      this._metadataCacheKey,
      '/metadata',
      this._metadataCacheTime,
      config
    );
  };
}

export const metadataApiService = new MetadataApiService();
